import { cn } from "utils/cn";
import { ProjectState } from "hooks/useProjects/useProjects.types";
import { productionTypeMapping, getProductionTypeEdgeColor } from "../../ProjectsPage.constants";

export interface ProjectPosterProps {
  project: Pick<ProjectState, "name" | "productionType" | "projectImagePath" | "archived">;
  className?: string;
}

// A clapperboard band, drawn with the foreground token so it works in every theme.
const slateStripes = {
  backgroundImage:
    "repeating-linear-gradient(135deg, rgb(var(--foreground) / 0.14) 0 12px, transparent 12px 24px)",
};

/** Production type as a label with a small colored dot: the only color on a poster. */
const TypeTag = ({ productionType, label }: { productionType: string; label: string }) => (
  <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.25em] text-foreground/50">
    <span
      aria-hidden
      className={cn("h-1.5 w-1.5 rounded-full", getProductionTypeEdgeColor(productionType))}
    />
    {label}
  </span>
);

/**
 * Key art for a project: its image when one is set, otherwise a neutral
 * typographic poster.
 */
export const ProjectPoster = ({ project, className }: ProjectPosterProps) => {
  const typeLabel = productionTypeMapping[project.productionType] || project.productionType;

  return (
    <div
      className={cn(
        "relative isolate overflow-hidden bg-card",
        project.archived && "grayscale",
        className
      )}
    >
      {project.projectImagePath ? (
        <>
          <img
            src={project.projectImagePath}
            alt=""
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            decoding="async"
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent"
          />
          <span className="absolute inset-x-0 bottom-0 line-clamp-2 p-4 font-squada text-2xl uppercase leading-[0.95] text-white">
            {project.name}
          </span>
        </>
      ) : (
        <>
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-br from-muted/70 via-card to-card"
          />
          <div aria-hidden className="absolute inset-x-0 top-0 h-2.5" style={slateStripes} />
          <div className="absolute inset-0 flex flex-col justify-end gap-1 p-4 transition-transform duration-500 group-hover:scale-[1.02]">
            {typeLabel && <TypeTag productionType={project.productionType} label={typeLabel} />}
            <span className="line-clamp-2 font-squada text-3xl uppercase leading-[0.9] text-foreground/85 [overflow-wrap:anywhere]">
              {project.name}
            </span>
          </div>
        </>
      )}
    </div>
  );
};
